import {
  AssignmentOutlined,
  ExpandMoreOutlined,
  FactCheckOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  MoveToInboxOutlined,
  RuleOutlined,
  RefreshOutlined,
  ShoppingBasketOutlined,
  WarningAmberOutlined,
  WarehouseOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  CurrentUser,
  loadApplicationSettings,
  loadAsns,
  loadDeliveryMissions,
  loadPutawayAlerts,
  loadPutawayTasks,
  loadPutawayTours,
  loadPutawayWorkQueues,
  loadProcessTransfers,
  loadPreparations,
  loadQualityInspections,
  loadRepackagingTasks,
  loadStockAllocations,
  loadStockByItem
} from "./api";
import { operationalStatusColor, sentenceCaseLabel } from "./operationalStatus";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";
import { StockoutRiskDashboard } from "./StockoutRiskDashboard";
import { HomeDashboardSelector } from "./HomeDashboardSelector";
import { ReplenishmentFollowUpDashboard } from "./ReplenishmentFollowUpDashboard";
import { ExpiryRiskDashboard } from "./ExpiryRiskDashboard";
import { StockoutRiskV2Dashboard } from "./StockoutRiskV2Dashboard";

type DashboardView =
  "inbound" | "stock" | "allocations" | "preparations" | "putaway" | "quality" |
  "repackaging" | "products" | "suppliers" | "packaging" |
  "warehouse" | "configuration" | "deliveries" | "outbound-orders";

interface OperationalDashboardProps {
  profile?: CurrentUser;
  onOpenMaterial: (itemId: string) => void;
  onNavigate: (
    view: DashboardView,
    targetTab?: "requests" | "queues" | "tours" | "tasks" | "alerts" | "closed" | "open" | "progress",
    taskId?: string,
    openDetail?: boolean
  ) => void;
}

interface WorkItem {
  id: string;
  title: string;
  context: string;
  age: string;
  status: string;
  lifecycle: "open" | "progress";
  action: string;
  view: DashboardView;
  putawayTab?: "requests" | "queues" | "tours" | "tasks" | "alerts" | "closed";
  preparationTab?: "open" | "progress";
  taskId?: string;
  actionKey: string;
  tone: "normal" | "warning" | "critical";
  icon: ReactNode;
  details?: Array<{ primary: string; secondary: string }>;
}

const openTaskStatuses = new Set([
  "WAITING_TRANSFER", "OPEN", "ASSIGNED", "IN_PROGRESS", "CREATED", "CLAIMED"
]);

function activityLifecycle(status: string): "open" | "progress" {
  return new Set([
    "EXPECTED", "CUSTOMS_PENDING", "ARRIVED", "PENDING", "PROPOSED", "OPEN", "CREATED",
    "WAITING_TRANSFER"
  ]).has(status) ? "open" : "progress";
}

function isToday(value: string | null | undefined): boolean {
  if (!value) return false;
  const date = new Date(value);
  const today = new Date();
  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate();
}

function localDayKey(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function lifecycleDetail(items: WorkItem[], t: TFunction): string {
  const labels: Partial<Record<DashboardView, string>> = {
    inbound: t("dashboard.activity.inbound"), putaway: t("dashboard.activity.putaway"),
    quality: t("dashboard.activity.quality"), repackaging: t("dashboard.activity.repackaging"),
    allocations: t("dashboard.activity.allocations"), preparations: t("dashboard.activity.preparations"),
    deliveries: t("dashboard.activity.deliveries")
  };
  const counts = items.reduce<Record<string, number>>((result, item) => {
    const label = labels[item.view] ?? t("dashboard.activity.other");
    result[label] = (result[label] ?? 0) + 1;
    return result;
  }, {});
  const parts = Object.entries(counts)
    .map(([label, count]) => `${count} ${label}${count > 1 ? "s" : ""}`);
  return parts.length ? parts.join(" · ") : t("dashboard.activity.none");
}

function elapsed(value: string | null | undefined, t: TFunction): string {
  if (!value) return t("dashboard.elapsed.pending");
  const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 60000));
  if (minutes < 1) return t("dashboard.elapsed.now");
  if (minutes < 60) return t("dashboard.elapsed.minutes", { count: minutes });
  const hours = Math.floor(minutes / 60);
  return t("dashboard.elapsed.hours", { hours, minutes: minutes % 60 });
}

function statusLabel(status: string, t: TFunction): string {
  const key = `dashboard.status.${status}`;
  return sentenceCaseLabel(t(key, { defaultValue: status }));
}

function MetricCard({
  label, value, detail, tone, icon, active, onClick
}: {
  label: string;
  value: number;
  detail: string;
  tone: "blue" | "gray" | "green" | "red";
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  const color = tone === "red" ? "error.main"
    : tone === "green" ? "#17856f"
      : tone === "gray" ? "#64748b" : "primary.main";
  const tint = tone === "red" ? "#fff2f2"
    : tone === "green" ? "#edf8f4"
      : tone === "gray" ? "#f2f5f8" : "#eef4fa";
  return (
    <Card onClick={onClick} role="button" tabIndex={0} aria-pressed={active}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        height: "100%", cursor: "pointer", position: "relative",
        border: active ? 2 : 1,
        borderColor: active ? color : "divider",
        borderTopWidth: active ? 4 : 3,
        borderTopColor: color,
        bgcolor: active ? tint : "background.paper",
        boxShadow: active ? 5 : 1,
        transform: active ? "translateY(-3px)" : "none",
        transition: "transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease, border-color 140ms ease",
        "&::after": active ? {
          content: '""', position: "absolute", left: "28%", right: "28%",
          bottom: -2, height: 5, borderRadius: "5px 5px 0 0", bgcolor: color
        } : undefined,
        "&:hover": { transform: "translateY(-3px)", boxShadow: active ? 6 : 3 },
        "&:focus-visible": { outline: "3px solid", outlineColor: color, outlineOffset: 2 }
      }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2.25 }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: "50%", display: "grid",
          placeItems: "center", color, bgcolor: tint,
          border: 1, borderColor: "divider"
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>{label}</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1.15 }}>
            {value}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ color }}>
            {detail}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

function WorkRow({ item, onNavigate, directAction }: {
  item: WorkItem;
  directAction: boolean;
  onNavigate: (
    view: DashboardView,
    targetTab?: "requests" | "queues" | "tours" | "tasks" | "alerts" | "closed" | "open" | "progress",
    taskId?: string,
    openDetail?: boolean
  ) => void;
}) {
  const { t } = useTranslation();
  const critical = item.tone === "critical";
  const [expanded, setExpanded] = useState(false);
  const expandable = Boolean(item.details?.length);
  return (
    <Box sx={{ borderTop: 1, borderColor: "divider", bgcolor: critical ? "error.50" : "transparent" }}>
      <Box sx={{
        minHeight: 68, px: { xs: 1.5, md: 2 }, py: 1.25,
        display: "grid",
        gridTemplateColumns: {
          xs: "auto minmax(0, 1fr) auto auto",
          md: "auto minmax(280px, 1.3fr) 1fr auto auto auto"
        },
        gap: { xs: 1, md: 2 }, alignItems: "center"
      }}>
        <Box sx={{ color: critical ? "error.main" : "secondary.main", display: "flex" }}>
          {item.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>{item.id}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{item.title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary"
          sx={{ display: { xs: "none", md: "block" } }}>
          {item.context} · {item.age}
        </Typography>
        <Button variant="outlined" color={critical ? "error" : "primary"}
          onClick={() => onNavigate(
            item.view, item.preparationTab ?? item.putawayTab,
            item.taskId,
            directAction
          )}
          sx={{ minWidth: 126, height: 36, display: { xs: "none", sm: "inline-flex" } }}>
          {item.action}
        </Button>
        <Chip label={sentenceCaseLabel(item.status)}
          color={critical ? "error" : operationalStatusColor(item.status)}
          variant="outlined" sx={{ minWidth: 120, height: 36, fontWeight: 800 }} />
        <IconButton size="small" disabled={!expandable}
          aria-label={expanded ? t("dashboard.actions.collapse") : t("dashboard.actions.expand")}
          onClick={() => setExpanded((value) => !value)}
          sx={{ visibility: expandable ? "visible" : "hidden", transform: expanded ? "rotate(180deg)" : "none" }}>
          <ExpandMoreOutlined />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Stack spacing={0} sx={{ mx: 2, mb: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
          {item.details?.map((detail, index) => (
            <Stack key={`${detail.primary}-${index}`} direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between" spacing={1}
              sx={{ px: 1.5, py: 1, borderTop: index ? 1 : 0, borderColor: "divider" }}>
              <Typography variant="body2" fontWeight={750}>{detail.primary}</Typography>
              <Typography variant="body2" color="text.secondary">{detail.secondary}</Typography>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export function OperationalDashboard({
  profile, onNavigate, onOpenMaterial
}: OperationalDashboardProps) {
  const { t, i18n } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const [tab, setTab] = useState<"open" | "progress" | "completed" | "alerts">("open");
  const [homeView, setHomeView] = useState<"operations" | "stock-risk" | "stock-risk-v2" | "replenishment" | "expiry-risk">("operations");
  const [replenishmentProposalId, setReplenishmentProposalId] = useState<string | null>(null);
  const permissions = profile?.permissions ?? [];
  const allowed = (permission: string) => permissions.includes(permission);
  const dashboardRefreshInterval = 60_000;

  const asns = useQuery({
    queryKey: ["asns"], queryFn: loadAsns, enabled: allowed("asn:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const putawayQueues = useQuery({
    queryKey: ["putaway-work-queues"], queryFn: loadPutawayWorkQueues,
    enabled: allowed("putaway_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const putawayTours = useQuery({
    queryKey: ["putaway-tours"], queryFn: loadPutawayTours,
    enabled: allowed("putaway_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const putawayTasks = useQuery({
    queryKey: ["putaway-tasks"], queryFn: loadPutawayTasks,
    enabled: allowed("putaway_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const transfers = useQuery({
    queryKey: ["process-transfers"], queryFn: loadProcessTransfers,
    enabled: allowed("process_transfer:read")
      || allowed("putaway_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const alerts = useQuery({
    queryKey: ["putaway-alerts"], queryFn: loadPutawayAlerts,
    enabled: allowed("putaway_alert:read") || allowed("putaway_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const quality = useQuery({
    queryKey: ["quality-inspections"], queryFn: loadQualityInspections,
    enabled: allowed("quality_inspection:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const repackaging = useQuery({
    queryKey: ["repackaging-tasks"], queryFn: loadRepackagingTasks,
    enabled: allowed("repackaging_task:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const allocations = useQuery({
    queryKey: ["stock-allocations"], queryFn: loadStockAllocations,
    enabled: allowed("allocation:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const preparations = useQuery({
    queryKey: ["preparations"], queryFn: loadPreparations,
    enabled: allowed("picking:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const deliveries = useQuery({
    queryKey: ["delivery-missions"], queryFn: loadDeliveryMissions,
    enabled: allowed("delivery:read"),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: dashboardRefreshInterval
  });
  const stock = useQuery({
    queryKey: ["stock-by-item"], queryFn: loadStockByItem,
    enabled: allowed("inventory:read"),
    refetchInterval: dashboardRefreshInterval
  });
  const activitySettings = useQuery({
    queryKey: ["application-settings"], queryFn: loadApplicationSettings,
    enabled: Boolean(profile),
    refetchInterval: dashboardRefreshInterval
  });
  const dashboardQueries = [asns, putawayQueues, putawayTours, putawayTasks,
    transfers, alerts, quality, repackaging, allocations, preparations,
    deliveries, stock, activitySettings];
  const dashboardUpdatedAt = Math.max(0, ...dashboardQueries.map((query) => query.dataUpdatedAt));
  const refreshDashboard = () => void Promise.all(dashboardQueries.map((query) => query.refetch()));
  const updatedTime = new Date(dashboardUpdatedAt || Date.now()).toLocaleTimeString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", {
      hour: "2-digit", minute: "2-digit"
    });
  const navigationModes = useMemo(() => new Map(
    (activitySettings.data ?? [])
      .filter((setting) => setting.category === "ACTIVITY_NAVIGATION")
      .map((setting) => [setting.key, setting.value])
  ), [activitySettings.data]);

  const receivingAsns = (asns.data ?? [])
    .filter((asn) => ["CUSTOMS_PENDING", "ARRIVED", "RECEIVING"].includes(asn.status));
  // Keep these filters identical to the "Tâches" and "En cours" tabs.
  const waitingQueues = (putawayQueues.data ?? [])
    .filter((queue) => queue.waitingTaskCount > 0);
  const activeTours = (putawayTours.data ?? [])
    .filter((tour) => ["READY", "LOADING", "IN_PROGRESS"].includes(tour.status));
  const activeAlerts = (alerts.data ?? [])
    .filter((alert) => !["RESOLVED", "CANCELLED"].includes(alert.status));
  const pendingTransfers = (transfers.data ?? [])
    .filter((task) => task.status === "OPEN");
  const activeTransfers = (transfers.data ?? [])
    .filter((task) => task.status === "IN_PROGRESS");
  const exceptionTransfers = (transfers.data ?? [])
    .filter((task) => task.status === "EXCEPTION");
  const activeQuality = (quality.data ?? [])
    .filter((task) => openTaskStatuses.has(task.status));
  const activeRepackaging = (repackaging.data ?? [])
    .filter((task) => openTaskStatuses.has(task.status));
  const exceptionRepackaging = (repackaging.data ?? [])
    .filter((task) => task.status === "EXCEPTION");
  const openAllocations = (allocations.data ?? [])
    .filter((allocation) => allocation.status === "OPEN");
  const preparationTasks = (preparations.data ?? []).flatMap(
    (order) => order.tasks
  );
  const activePreparations = preparationTasks.filter(
    (task) => ["OPEN", "IN_PROGRESS"].includes(task.status)
  );
  const activeDeliveries = (deliveries.data ?? []).filter(
    (mission) => ["OPEN", "IN_PROGRESS"].includes(mission.status)
  );
  const exceptionDeliveries = (deliveries.data ?? []).filter(
    (mission) => mission.status === "EXCEPTION"
  );

  const workItems = useMemo<WorkItem[]>(() => [
    ...receivingAsns.map((asn): WorkItem => ({
      id: asn.asnNumber,
      title: asn.supplierName,
      context: t("dashboard.details.expectedArrival", { warehouse: asn.warehouseCode, date: asn.expectedArrivalAt
        ? new Date(asn.expectedArrivalAt).toLocaleString(i18n.language, {
          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
        }) : t("dashboard.details.unscheduled") }),
      age: elapsed(asn.updatedAt, t),
      status: statusLabel(asn.status, t),
      lifecycle: activityLifecycle(asn.status),
      action: t("dashboard.actions.view"),
      actionKey: asn.status === "CUSTOMS_PENDING"
        ? "ACTIVITY_NAVIGATION_CUSTOMS_CLEARANCE"
        : "ACTIVITY_NAVIGATION_RECEIVING_VIEW",
      view: "inbound",
      taskId: asn.id,
      tone: "normal",
      icon: <MoveToInboxOutlined />
    })),
    ...waitingQueues.map((queue): WorkItem => ({
      id: queue.name,
      title: t("dashboard.details.waitingHu", { count: queue.waitingTaskCount }),
      context: `${formatMeasurement(queue.waitingWeightKg, "MASS",
        measurements, i18n.language)} · ${queue.routingStrategy === "LOCATION_SEQUENCE"
        ? t("dashboard.details.optimized") : t("dashboard.details.free")}`,
      age: elapsed(queue.oldestTaskAt, t),
      status: t("dashboard.status.build"),
      lifecycle: "open",
      action: t("dashboard.details.build"),
      actionKey: "ACTIVITY_NAVIGATION_PUTAWAY_QUEUE_VIEW",
      view: "putaway",
      putawayTab: "queues",
      taskId: queue.id,
      tone: "normal",
      icon: <RuleOutlined />
    })),
    ...activeTours.map((tour): WorkItem => ({
      id: tour.number,
      title: `${tour.executionMode === "DIRECT" ? t("dashboard.details.direct") : tour.workQueueName} · ${tour.steps.length} HU`,
      context: t("dashboard.details.rollDistance", { roll: tour.executionMode === "ROLL" ? `Roll ${tour.equipmentNumber ?? t("dashboard.details.rollScan")}` : t("dashboard.details.noRoll"), distance: tour.estimatedDistanceUnits }),
      age: elapsed(tour.startedAt ?? tour.createdAt, t),
      status: tour.status === "READY" ? t("dashboard.status.ready") : tour.status === "LOADING" ? t("dashboard.status.loading") : t("dashboard.status.IN_PROGRESS"),
      lifecycle: tour.status === "IN_PROGRESS" ? "progress" : "open",
      action: tour.status === "IN_PROGRESS" ? t("dashboard.actions.continue") : t("dashboard.actions.start"),
      actionKey: "ACTIVITY_NAVIGATION_PUTAWAY_TOUR_EXECUTE",
      view: "putaway",
      putawayTab: "tours",
      taskId: tour.id,
      tone: "normal",
      icon: <RuleOutlined />,
      details: tour.steps.map((step) => ({
        primary: `${step.sequence}. ${step.huNumber}`,
        secondary: `${step.destinationBinCode} · ${t("dashboard.details.distance", { distance: step.distanceFromPrevious })}`
      }))
    })),
    ...[...pendingTransfers, ...activeTransfers].map((task): WorkItem => ({
      id: task.huNumber,
      title: t("dashboard.operation.transfer", { destination: task.destinationPointCode }),
      context: task.transferType === "PUTAWAY_TO_QUALITY"
        ? t("dashboard.details.transferToQuality", { source: task.sourcePointCode })
        : task.transferType === "PICKING_TO_OUTBOUND_REPACKAGING"
          ? t("dashboard.details.pickingToRepackaging", { source: task.sourcePointCode })
          : t("dashboard.details.qualityToRepackaging", { source: task.sourcePointCode }),
      age: elapsed(task.startedAt ?? task.createdAt, t),
      status: t(`dashboard.status.${task.status === "OPEN" ? "OPEN" : "IN_PROGRESS"}`),
      lifecycle: activityLifecycle(task.status),
      action: task.status === "OPEN" ? t("dashboard.actions.claim") : t("dashboard.actions.view"),
      actionKey: task.status === "OPEN"
        ? "ACTIVITY_NAVIGATION_PROCESS_TRANSFER_CLAIM"
        : "ACTIVITY_NAVIGATION_PROCESS_TRANSFER_COMPLETE",
      view: "putaway",
      putawayTab: "tasks",
      taskId: task.id,
      tone: "normal",
      icon: <RuleOutlined />
    })),
    ...activeQuality.map((task): WorkItem => ({
      id: task.huNumber,
      title: t("dashboard.operation.quality", { reason: task.reasonCode }),
      context: `${task.warehouseCode} · ${task.processPointCode}`,
      age: elapsed(task.createdAt, t),
      status: statusLabel(task.status, t),
      lifecycle: activityLifecycle(task.status),
      action: t("dashboard.actions.view"),
      actionKey: task.status === "OPEN"
        ? "ACTIVITY_NAVIGATION_QUALITY_INSPECTION_CLAIM"
        : "ACTIVITY_NAVIGATION_QUALITY_INSPECTION_DECIDE",
      view: "quality",
      taskId: task.id,
      tone: "warning",
      icon: <FactCheckOutlined />
    })),
    ...activeRepackaging.map((task): WorkItem => ({
      id: task.huNumber,
      title: t("dashboard.operation.repackaging", { item: task.itemCode }),
      context: `${task.warehouseCode} · ${task.processPointCode}`,
      age: elapsed(task.createdAt, t),
      status: statusLabel(task.status, t),
      lifecycle: activityLifecycle(task.status),
      action: t("dashboard.actions.view"),
      actionKey: task.status === "OPEN"
        ? "ACTIVITY_NAVIGATION_REPACKAGING_CLAIM"
        : "ACTIVITY_NAVIGATION_REPACKAGING_COMPLETE",
      view: "repackaging",
      taskId: task.id,
      tone: "warning",
      icon: <Inventory2Outlined />
    })),
    ...openAllocations.map((allocation): WorkItem => ({
      id: allocation.requestReference,
      title: t("dashboard.operation.allocation", { item: allocation.itemCode }),
      context: `${allocation.warehouseCode} · ${allocation.requestedQuantity} ${allocation.uom}`,
      age: elapsed(allocation.createdAt, t),
      status: t("dashboard.status.OPEN"),
      lifecycle: "open",
      action: t("dashboard.actions.view"),
      actionKey: "ACTIVITY_NAVIGATION_ALLOCATION_RELEASE",
      view: "allocations",
      taskId: allocation.id,
      tone: "normal",
      icon: <AssignmentOutlined />
    })),
    ...activePreparations.map((task): WorkItem => ({
      id: task.handlingUnitNumber,
      title: t("dashboard.operation.preparation", { item: task.itemCode }),
      context: `${task.sourceLocationCode} → ${task.destinationProcessPointCode}`,
      age: elapsed(task.startedAt ?? task.createdAt, t),
      status: statusLabel(task.status, t),
      lifecycle: activityLifecycle(task.status),
      action: task.status === "OPEN" ? t("dashboard.actions.claim") : t("dashboard.actions.confirm"),
      actionKey: task.status === "OPEN"
        ? "ACTIVITY_NAVIGATION_PICKING_TASK_CLAIM"
        : task.status === "EXCEPTION"
          ? "ACTIVITY_NAVIGATION_PICKING_EXCEPTION_VIEW"
          : "ACTIVITY_NAVIGATION_PICKING_TASK_COMPLETE",
      view: "preparations",
      preparationTab: task.status === "OPEN" ? "open" : "progress",
      taskId: task.id,
      tone: "normal",
      icon: <ShoppingBasketOutlined />
    })),
    ...activeDeliveries.map((mission): WorkItem => ({
      id: mission.trolleyNumber ?? mission.missionNumber,
      title: t("dashboard.operation.delivery", { destination: mission.destinationCode }),
      context: `${mission.lines.length} HU · ${mission.clientCode}`,
      age: elapsed(mission.startedAt ?? mission.createdAt, t),
      status: statusLabel(mission.status, t),
      lifecycle: activityLifecycle(mission.status),
      action: mission.status === "OPEN" ? t("dashboard.actions.claim") : t("dashboard.actions.confirm"),
      actionKey: mission.status === "OPEN"
        ? "ACTIVITY_NAVIGATION_DELIVERY_MISSION_CLAIM"
        : "ACTIVITY_NAVIGATION_DELIVERY_MISSION_COMPLETE",
      view: "deliveries",
      taskId: mission.id,
      tone: "normal",
      icon: <LocalShippingOutlined />
    }))
  ], [
    activeTours, activeQuality, activeRepackaging, activeTransfers,
    pendingTransfers, waitingQueues, receivingAsns, openAllocations,
    activePreparations, activeDeliveries, t, i18n.language, measurements
  ]);

  const completedItems = useMemo<WorkItem[]>(() => [
    ...(asns.data ?? []).filter((asn) => asn.status === "RECEIVED" && isToday(asn.updatedAt))
      .map((asn): WorkItem => ({
        id: asn.asnNumber, title: asn.supplierName,
        context: asn.warehouseCode, age: t("dashboard.elapsed.today"),
        status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_RECEIVING_VIEW", view: "inbound",
        taskId: asn.id,
        tone: "normal", icon: <MoveToInboxOutlined />,
        details: [
          { primary: t("dashboard.details.receipts"), secondary: asn.receiptNumbers ?? t("dashboard.details.noReceipt") },
          { primary: t("dashboard.details.huCreated"), secondary: asn.handlingUnitNumbers ?? t("dashboard.details.noHu") }
        ]
      })),
    ...(putawayTours.data ?? []).filter((tour) => tour.status === "COMPLETED" && isToday(tour.completedAt))
      .map((tour): WorkItem => ({
        id: tour.number,
        title: t("dashboard.details.completed", { name: tour.executionMode === "DIRECT" ? t("dashboard.details.direct") : tour.workQueueName }),
        context: t("dashboard.details.huDistance", { count: tour.steps.length, distance: tour.estimatedDistanceUnits }), age: t("dashboard.elapsed.today"),
        status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_PUTAWAY_TOUR_EXECUTE", view: "putaway",
        putawayTab: "tours", taskId: tour.id, tone: "normal", icon: <RuleOutlined />,
        details: tour.steps.map((step) => ({
          primary: `${step.sequence}. ${step.huNumber}`,
          secondary: `${step.destinationBinCode} · ${statusLabel(step.taskStatus, t)}`
        }))
      })),
    ...(transfers.data ?? []).filter((task) => task.status === "COMPLETED" && isToday(task.completedAt))
      .map((task): WorkItem => ({
        id: task.huNumber, title: t("dashboard.operation.transfer", { destination: task.destinationPointCode }),
        context: task.sourcePointCode, age: t("dashboard.elapsed.today"),
        status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_PROCESS_TRANSFER_COMPLETE", view: "putaway",
        putawayTab: "closed", taskId: task.id, tone: "normal", icon: <RuleOutlined />,
        details: [{ primary: task.huNumber, secondary: `${task.sourcePointCode} → ${task.destinationPointCode}` }]
      })),
    ...(quality.data ?? []).filter((task) => task.status === "COMPLETED" && isToday(task.completedAt))
      .map((task): WorkItem => ({
        id: task.huNumber, title: t("dashboard.operation.quality", { reason: task.reasonCode }),
        context: task.processPointCode, age: t("dashboard.elapsed.today"),
        status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_QUALITY_INSPECTION_DECIDE", view: "quality",
        taskId: task.id, tone: "normal", icon: <FactCheckOutlined />,
        details: [{ primary: task.huNumber, secondary: `${task.reasonCode.replaceAll("_", " ")} · ${task.processPointCode}` }]
      })),
    ...(repackaging.data ?? []).filter((task) => task.status === "COMPLETED" && isToday(task.completedAt))
      .map((task): WorkItem => ({
        id: task.huNumber, title: t("dashboard.operation.repackaging", { item: task.itemCode }),
        context: task.processPointCode, age: t("dashboard.elapsed.today"),
        status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_REPACKAGING_COMPLETE", view: "repackaging",
        taskId: task.id, tone: "normal", icon: <Inventory2Outlined />,
        details: [{ primary: `${task.huNumber} · ${task.itemCode}`, secondary: task.processPointCode }]
      })),
    ...preparationTasks.filter((task) => task.status === "COMPLETED" && isToday(task.completedAt))
      .map((task): WorkItem => ({
        id: task.handlingUnitNumber, title: t("dashboard.operation.preparation", { item: task.itemCode }),
        context: `${task.sourceLocationCode} → ${task.destinationProcessPointCode}`,
        age: t("dashboard.elapsed.today"), status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_PICKING_TASK_COMPLETE", view: "preparations",
        preparationTab: "progress", taskId: task.id, tone: "normal",
        icon: <ShoppingBasketOutlined />,
        details: [{
          primary: `${task.handlingUnitNumber} · ${task.itemCode}`,
          secondary: `${task.pickedQuantity} ${task.uom} · ${task.sourceLocationCode} → ${task.destinationProcessPointCode}`
        }]
      })),
    ...(deliveries.data ?? []).filter((mission) => mission.status === "COMPLETED" && isToday(mission.completedAt))
      .map((mission): WorkItem => ({
        id: mission.trolleyNumber ?? mission.missionNumber,
        title: t("dashboard.operation.delivery", { destination: mission.destinationCode }),
        context: `${mission.lines.length} HU · ${mission.clientCode}`,
        age: t("dashboard.elapsed.today"), status: t("dashboard.status.completed"), lifecycle: "progress", action: t("dashboard.actions.view"),
        actionKey: "ACTIVITY_NAVIGATION_DELIVERY_MISSION_COMPLETE", view: "deliveries",
        taskId: mission.id, tone: "normal", icon: <LocalShippingOutlined />,
        details: mission.lines.map((line) => ({
          primary: `${line.handlingUnitNumber} · ${line.itemCode}`,
          secondary: `${line.quantity} ${line.uom} · ${line.outboundOrderNumber ?? t("dashboard.details.noOutboundOrder")}`
        }))
      }))
  ], [asns.data, putawayTours.data, transfers.data, quality.data,
    repackaging.data, preparationTasks, deliveries.data, t]);

  const alertItems = useMemo<WorkItem[]>(() => [
    ...activeAlerts.map((alert): WorkItem => ({
      id: alert.huNumber,
      title: alert.comment || alert.reasonCode.replaceAll("_", " "),
      context: alert.rejectedBinCode
        ? t("dashboard.details.rejectedLocation", { location: alert.rejectedBinCode })
        : t("dashboard.details.beforeLocationProposal"),
      age: elapsed(alert.reportedAt, t),
      status: t("dashboard.status.handle"),
      lifecycle: "open",
      action: alert.resolutionAction === "PACKAGING_IDENTIFICATION"
        ? t("dashboard.actions.specify") : t("dashboard.actions.view"),
      actionKey: alert.resolutionAction === "PACKAGING_IDENTIFICATION"
        ? "ACTIVITY_NAVIGATION_PUTAWAY_ALERT_IDENTIFY_PACKAGING"
        : "ACTIVITY_NAVIGATION_PUTAWAY_ALERT_REPLAN",
      view: "putaway",
      putawayTab: "alerts",
      taskId: alert.id,
      tone: "critical",
      icon: <WarningAmberOutlined />
    })),
    ...exceptionTransfers.map((task): WorkItem => ({
      id: task.huNumber,
      title: task.exceptionComment
        || task.exceptionCode?.replaceAll("_", " ")
        || t("dashboard.details.transferException"),
      context: `${task.sourcePointCode} → ${task.destinationPointCode}`,
      age: elapsed(task.exceptionReportedAt ?? task.createdAt, t),
      status: t("dashboard.status.handle"),
      lifecycle: "open",
      action: t("dashboard.actions.handle"),
      actionKey: "ACTIVITY_NAVIGATION_PROCESS_TRANSFER_EXCEPTION",
      view: "putaway",
      putawayTab: "alerts",
      taskId: task.id,
      tone: "critical",
      icon: <WarningAmberOutlined />
    })),
    ...exceptionRepackaging.map((task): WorkItem => ({
      id: task.huNumber,
      title: task.exceptionComment
        || task.exceptionCode?.replaceAll("_", " ")
        || t("dashboard.details.repackagingException"),
      context: `${task.itemCode} · ${task.warehouseCode} · ${task.processPointCode}`,
      age: elapsed(task.exceptionReportedAt ?? task.createdAt, t),
      status: t("dashboard.status.handle"),
      lifecycle: "open",
      action: t("dashboard.actions.handle"),
      actionKey: "ACTIVITY_NAVIGATION_REPACKAGING_COMPLETE",
      view: "repackaging",
      taskId: task.id,
      tone: "critical",
      icon: <WarningAmberOutlined />
    })),
    ...exceptionDeliveries.map((mission): WorkItem => ({
      id: mission.trolleyNumber ?? mission.missionNumber,
      title: t("dashboard.details.deliveryException", { destination: mission.destinationCode }),
      context: `${mission.lines.length} HU · ${mission.clientCode}`,
      age: elapsed(mission.startedAt ?? mission.createdAt, t),
      status: t("dashboard.status.handle"),
      lifecycle: "open",
      action: t("dashboard.actions.handle"),
      actionKey: "ACTIVITY_NAVIGATION_DELIVERY_MISSION_COMPLETE",
      view: "deliveries",
      taskId: mission.id,
      tone: "critical",
      icon: <WarningAmberOutlined />
    }))
  ], [activeAlerts, exceptionTransfers, exceptionRepackaging, exceptionDeliveries, t]);

  const openItems = workItems.filter((item) => item.lifecycle === "open");
  const progressItems = workItems.filter((item) => item.lifecycle === "progress");
  const activityByDay = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - (6 - index));
      return {
        key: localDayKey(date),
        label: date.toLocaleDateString(i18n.language.startsWith("en") ? "en-US" : "fr-FR", { weekday: "short" }).replace(".", ""),
        receptions: 0, putaway: 0, preparations: 0, deliveries: 0
      };
    });
    const byKey = new Map(days.map((day) => [day.key, day]));
    (asns.data ?? [])
      .filter((asn) => ["RECEIVED", "CLOSED"].includes(asn.status))
      .forEach((asn) => { const day = byKey.get(localDayKey(asn.updatedAt)); if (day) day.receptions += 1; });
    (putawayTasks.data ?? [])
      .filter((task) => task.status === "COMPLETED" && task.completedAt)
      .forEach((task) => { const day = byKey.get(localDayKey(task.completedAt!)); if (day) day.putaway += 1; });
    preparationTasks
      .filter((task) => task.status === "COMPLETED" && task.completedAt)
      .forEach((task) => { const day = byKey.get(localDayKey(task.completedAt!)); if (day) day.preparations += 1; });
    (deliveries.data ?? [])
      .filter((mission) => mission.status === "COMPLETED" && mission.completedAt)
      .forEach((mission) => { const day = byKey.get(localDayKey(mission.completedAt!)); if (day) day.deliveries += 1; });
    return days;
  }, [asns.data, putawayTasks.data, preparationTasks, deliveries.data]);
  const activityMaximum = Math.max(1, ...activityByDay.flatMap((day) => [
    day.receptions, day.putaway, day.preparations, day.deliveries
  ]));
  const quarantined = (stock.data ?? [])
    .reduce((total, item) => total + item.quarantinedQuantity, 0);
  const rejected = (stock.data ?? [])
    .reduce((total, item) => total + item.rejectedQuantity, 0);
  const stockWatch = [
    {
      label: t("dashboard.monitoring.quarantine"), compactLabel: t("dashboard.monitoring.quarantine"),
      value: quarantined, color: "warning.main", view: "stock" as const
    },
    {
      label: t("dashboard.monitoring.rejected"), compactLabel: t("dashboard.monitoring.rejectedShort"),
      value: rejected, color: "error.main", view: "stock" as const
    },
    {
      label: t("dashboard.monitoring.arrangementExceptions"), compactLabel: t("dashboard.monitoring.exceptions"),
      value: activeAlerts.length + exceptionTransfers.length
        + exceptionRepackaging.length + exceptionDeliveries.length,
      color: "error.main", view: "putaway" as const
    }
  ];
  const visibleItems = tab === "open" ? openItems
    : tab === "progress" ? progressItems
      : tab === "completed" ? completedItems : alertItems;
  const mobileTabs = [
    { value: "open" as const, label: t("dashboard.tabs.open"), mobileLabel: t("dashboard.tabs.open").toLocaleUpperCase(i18n.language),
      count: openItems.length },
    { value: "progress" as const, label: t("dashboard.tabs.progress"), mobileLabel: t("dashboard.tabs.progress").toLocaleUpperCase(i18n.language),
      count: progressItems.length },
    { value: "completed" as const, label: t("dashboard.tabs.completed"), mobileLabel: t("dashboard.tabs.completed").toLocaleUpperCase(i18n.language),
      count: completedItems.length },
    { value: "alerts" as const, label: t("dashboard.tabs.alerts"), mobileLabel: t("dashboard.tabs.alerts").toLocaleUpperCase(i18n.language),
      count: alertItems.length }
  ];

  if (homeView === "stock-risk") {
    return <StockoutRiskDashboard canRead={allowed("stock_risk:read")}
      canReviewMaterials={allowed("replenishment:review")}
      canApproveProposals={allowed("replenishment:approve")}
      onShowOperations={() => setHomeView("operations")}
      operationsLabel={t("dashboard.title")}
      onShowReplenishment={allowed("replenishment:read")
        ? () => setHomeView("replenishment") : undefined}
      onShowStockRiskV2={allowed("stock_risk_v2:read")
        ? () => setHomeView("stock-risk-v2") : undefined}
      onReviewMaterials={onOpenMaterial} />;
  }
  if (homeView === "stock-risk-v2") {
    return <StockoutRiskV2Dashboard canRead={allowed("stock_risk_v2:read")}
      canReviewActions={allowed("replenishment:review")}
      canApproveActions={allowed("replenishment:approve")}
      onReviewAction={(proposalId) => {
        setReplenishmentProposalId(proposalId);
        setHomeView("replenishment");
      }}
      onChangeView={setHomeView} onOpenMaterial={onOpenMaterial}
      operationsLabel={t("dashboard.title")}
      showReplenishment={allowed("replenishment:read")}
      showExpiryRisk={allowed("expiry_risk:read")} />;
  }
  if (homeView === "replenishment") {
    return <ReplenishmentFollowUpDashboard
      canExecute={allowed("replenishment:execute")}
      initialProposalId={replenishmentProposalId}
      operationsLabel={t("dashboard.title")}
      showStockRiskV2={allowed("stock_risk_v2:read")}
      showExpiryRisk={allowed("expiry_risk:read")}
      onChangeView={setHomeView} />;
  }
  if (homeView === "expiry-risk") {
    return <ExpiryRiskDashboard canRead={allowed("expiry_risk:read")}
      canApprove={allowed("derogation:execute")}
      operationsLabel={t("dashboard.title")}
      showStockRiskV2={allowed("stock_risk_v2:read")}
      showReplenishment={allowed("replenishment:read")}
      onChangeView={setHomeView} />;
  }

  return (
    <>
    <Stack spacing={1} sx={{
      display: { xs: "flex", sm: "none" }, height: "100%", minHeight: 0,
      overflow: "hidden"
    }}>
      <Card variant="outlined" sx={{ p: 1.25, flexShrink: 0,
        borderTop: 3, borderTopColor: "secondary.main" }}>
        <Stack spacing={.75} sx={{ minWidth: 0 }}>
          {(allowed("stock_risk_v2:read") || allowed("replenishment:read")
            || allowed("expiry_risk:read")) ? <HomeDashboardSelector
            fullWidth value="operations" showStockRiskV2={allowed("stock_risk_v2:read")}
            operationsLabel={t("dashboard.title")}
            showReplenishment={allowed("replenishment:read")}
            showExpiryRisk={allowed("expiry_risk:read")}
            onChange={setHomeView} /> : (
              <Typography variant="h6" component="h1" fontWeight={850}>{t("dashboard.title")}</Typography>
            )}
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.subtitle")}
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
              {t("dashboard.updated", { time: updatedTime })}
            </Typography>
            <IconButton aria-label={t("dashboard.refresh")} sx={{ width: 44, height: 44, flexShrink: 0 }}
              onClick={refreshDashboard}><RefreshOutlined /></IconButton>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={.75} useFlexGap flexWrap="wrap"
          sx={{ mt: 1 }}>
          {stockWatch.map((entry) => (
            <Chip key={entry.label} size="small" clickable
              label={`${entry.compactLabel} ${entry.value}`}
              onClick={() => onNavigate(entry.view)}
              sx={{ color: entry.color, fontWeight: 750 }} />
          ))}
        </Stack>
      </Card>
      <ResponsiveStatusTabs value={tab} items={mobileTabs} onChange={setTab} />
      <Stack spacing={1} sx={{ flex: "1 1 0", minHeight: 0,
        overflowY: "auto", pb: 1, pr: .25,
        "& > *": { flexShrink: 0 } }}>
        {visibleItems.length ? visibleItems.map((item) => {
          const directAction = tab !== "completed"
            && (navigationModes.get(item.actionKey) ?? "TARGETED_LIST")
              === "DIRECT_ACTION";
          return (
            <Card key={`${item.view}-${item.id}`} variant="outlined"
              sx={{ p: 1.25, borderLeft: 4,
                borderLeftColor: item.tone === "critical"
                  ? "error.main" : "secondary.main" }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ color: item.tone === "critical"
                    ? "error.main" : "secondary.main", display: "flex", pt: .25 }}>
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={850}>{item.id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.title}
                    </Typography>
                  </Box>
                  <Chip size="small" label={sentenceCaseLabel(item.status)}
                    color={item.tone === "critical"
                      ? "error" : operationalStatusColor(item.status)} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {item.context} · {item.age}
                </Typography>
                <Button size="small" variant="outlined"
                  color={item.tone === "critical" ? "error" : "primary"}
                  onClick={() => onNavigate(
                    item.view, item.preparationTab ?? item.putawayTab,
                    item.taskId, directAction
                  )}>
                  {item.action}
                </Button>
              </Stack>
            </Card>
          );
        }) : (
          <Alert severity={tab === "alerts" ? "success" : "info"}>
            {t(`dashboard.empty.${tab}`)}
          </Alert>
        )}
      </Stack>
    </Stack>
    <Box sx={{ display: { xs: "none", sm: "block" }, width: "100%",
      flex: 1, height: "100%", minHeight: 0, minWidth: 0 }}>
    <Stack spacing={1} sx={{
      width: "100%",
      minHeight: 0,
      height: { md: "100%" },
      "@media (min-width: 900px)": {
        flex: 1
      }
    }}>
      <Box>
        <Stack direction="row" justifyContent="space-between"
          alignItems="center" spacing={2}>
          <Typography variant="h4" component="h1" fontWeight={800}
            sx={{
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              letterSpacing: "-.02em"
            }}>
            {t("dashboard.title")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {(allowed("stock_risk_v2:read") || allowed("replenishment:read")
              || allowed("expiry_risk:read")) && <HomeDashboardSelector
              value="operations" showStockRiskV2={allowed("stock_risk_v2:read")}
              operationsLabel={t("dashboard.title")}
              showReplenishment={allowed("replenishment:read")}
              showExpiryRisk={allowed("expiry_risk:read")}
              onChange={setHomeView} />}
          <IconButton size="small" aria-label={t("dashboard.refresh")}
            onClick={refreshDashboard}><RefreshOutlined /></IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {t("dashboard.updated", { time: updatedTime })}
          </Typography>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard label={t("dashboard.metrics.open")} value={openItems.length}
            detail={lifecycleDetail(openItems, t)} tone="blue"
            icon={<AssignmentOutlined />} active={tab === "open"}
            onClick={() => setTab("open")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard label={t("dashboard.metrics.progress")} value={progressItems.length}
            detail={lifecycleDetail(progressItems, t)} tone="green"
            icon={<WarehouseOutlined />} active={tab === "progress"}
            onClick={() => setTab("progress")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard label={t("dashboard.metrics.completed")} value={completedItems.length}
            detail={lifecycleDetail(completedItems, t)} tone="gray"
            icon={<FactCheckOutlined />} active={tab === "completed"}
            onClick={() => setTab("completed")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard label={t("dashboard.metrics.alerts")} value={alertItems.length}
            detail={alertItems.length ? lifecycleDetail(alertItems, t) : t("dashboard.metrics.none")}
            tone="red" icon={<WarningAmberOutlined />}
            active={tab === "alerts"}
            onClick={() => setTab("alerts")} />
        </Grid>
      </Grid>

      <Card sx={{
        flex: "1 1 120px",
        minHeight: 96,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}>
        <Tabs value={tab}
          onChange={(_, value: "open" | "progress" | "completed" | "alerts") => setTab(value)}
          sx={{ px: 1.5, flexShrink: 0 }}>
          <Tab value="open" label={`${t("dashboard.tabs.open").toLocaleUpperCase(i18n.language)} (${openItems.length})`} />
          <Tab value="progress" label={`${t("dashboard.tabs.progress").toLocaleUpperCase(i18n.language)} (${progressItems.length})`} />
          <Tab value="completed" label={`${t("dashboard.tabs.completed").toLocaleUpperCase(i18n.language)} (${completedItems.length})`} />
          <Tab value="alerts" label={`${t("dashboard.tabs.alerts").toLocaleUpperCase(i18n.language)} (${alertItems.length})`} />
        </Tabs>
        <Box sx={{
          minHeight: 0,
          flex: 1,
          overflowY: "scroll",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable"
        }}>
          {visibleItems.length ? visibleItems.map((item) => (
            <WorkRow key={`${item.view}-${item.id}`} item={item}
              onNavigate={onNavigate}
              directAction={
                tab !== "completed" && (navigationModes.get(item.actionKey) ?? "TARGETED_LIST")
                  === "DIRECT_ACTION"
              } />
          )) : (
            <Box sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
              <Typography color="text.secondary">
                {t(`dashboard.empty.${tab}`)}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      <Grid container spacing={2} sx={{ flexShrink: 0 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card sx={{ height: "100%", p: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>{t("dashboard.history.title")}</Typography>
                <Typography variant="caption" color="text.secondary">{t("dashboard.history.subtitle")}</Typography>
              </Box>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                {[
                  [t("dashboard.history.receipts"), "primary.main"], [t("dashboard.history.putaway"), "secondary.main"],
                  [t("dashboard.history.preparations"), "success.main"], [t("dashboard.history.deliveries"), "warning.main"]
                ].map(([label, color]) => (
                  <Stack key={label} direction="row" spacing={.5} alignItems="center">
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: color }} />
                    <Typography variant="caption">{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Box sx={{ height: 118, mt: 1.25, display: "flex", gap: 1.25, alignItems: "stretch" }}>
              {activityByDay.map((day) => (
                <Stack key={day.key} alignItems="center" justifyContent="flex-end" sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="flex-end" justifyContent="center" spacing={.35}
                    sx={{ height: 88, width: "100%", borderBottom: 1, borderColor: "divider" }}>
                    {([
                      [day.receptions, "primary.main", "réceptions"],
                      [day.putaway, "secondary.main", "HU rangées"],
                      [day.preparations, "success.main", "HU préparées"],
                      [day.deliveries, "warning.main", "livraisons"]
                    ] as Array<[number, string, string]>).map(([value, color, label]) => (
                      <Box key={label} title={`${value} ${label}`} sx={{
                        width: { xs: 5, sm: 8 }, maxWidth: 10, borderRadius: "3px 3px 0 0",
                        bgcolor: color, opacity: value ? 1 : .12,
                        height: value ? `${Math.max(7, value / activityMaximum * 82)}px` : 2
                      }} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: .5, textTransform: "capitalize" }}>
                    {day.label}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: "100%", p: 2 }}>
            <Box sx={{ minHeight: 36, display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight={800}
                sx={{ whiteSpace: "nowrap" }}>
                {t("dashboard.monitoring.title")}
              </Typography>
            </Box>
            <Box sx={{ pt: 1 }}>
              {stockWatch.map((entry) => (
                <Stack key={entry.label} direction="row"
                  justifyContent="space-between"
                  onClick={() => onNavigate(entry.view)}
                  sx={{
                    py: 1.2, borderTop: 1, borderColor: "divider",
                    cursor: "pointer"
                  }}>
                  <Typography variant="body2">{entry.label}</Typography>
                  <Typography fontWeight={800} sx={{ color: entry.color }}>
                    {entry.value}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

    </Stack>
    </Box>
    </>
  );
}
